import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Box, Card, Fade, Flex, List, ListIcon, ListItem, Text } from '@chakra-ui/react';

import { BillingFrequency } from '~/features/checkout/models/checkout';
import { COUPON_COOKIE } from '~/features/cookies/constants';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { setClientCookie } from '~/features/cookies/utils/set-client-cookie';
import { getCouponCodeFromCookie } from '~/features/discount/utils/get-coupon-code-from-cookie';
import { isPersonalizedDiscountCoupon } from '~/features/discount/utils/is-pd-coupon';
import { useFreeTrialState } from '~/features/free-trial/hooks/use-free-trial-state';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { useIsEUVisitor } from '~/features/internationalization/hooks/use-is-eu-visitor';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { PriceCardSticker } from '~/features/promotions/components/price-card-sticker';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { CountdownConfig } from '~/features/promotions/models/countdown-config';
import { useClientRender } from '~/hooks/use-client-render';
import { parseMs, useCountdown } from '~/hooks/use-countdown';

import { CalendarIcon, CheckedIcon, FireIcon } from '../../ab_plp_redesign_2025/icons';

import { CountdownTimer } from './countdown-timer';

type ButtonRenderProp = (props?: {
  isCountdownActive?: boolean;
  isFallback?: boolean;
  couponPercentOff?: number | null;
  countdownConfig?: CountdownConfig | null;
  couponCode?: string | null;
  forcedButtonLabel?: string | null;
  until?: string;
  onButtonClick?: () => Promise<void>;
}) => ReactNode;

const defaultButtonLabel = 'Enroll Now';

// A stacked version of the countdown timer. Looks like this:
//
// |--------------------------------|
// |Custom countdown blurb | Timer  |
// |--------------------------------|
// |       [Enroll Now btn]         |
// |--------------------------------|
export function CountdownCompactContainer({
  renderButton,
  selectedPlan,
}: {
  renderButton: (selectedPlan: BillingFrequency, buttonLabel: string) => ReactNode;
  selectedPlan: BillingFrequency;
}) {
  const [isTabVisible, setIsTabVisible] = useState(true);
  const { data: promoData, status: promoStatus } = usePromotion();
  const clientReady = useClientRender();
  const freeTrial = useFreeTrialState();

  const countdownConfig = promoData?.countdownConfig;

  // Note: this effect is necessary to refresh the countdown when the browser tab is stale and is revisited.
  useEffect(() => {
    function onVisibilityChange() {
      setIsTabVisible(document.visibilityState === 'visible');
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const buttonLabel = useMemo(() => {
    switch (promoStatus) {
      case 'loading':
        return '';
      default:
        return defaultButtonLabel;
      case 'success':
        const customLabel = countdownConfig?.buttonLabelOverride;
        if (customLabel) {
          return customLabel;
        } else {
          return defaultButtonLabel;
        }
    }
  }, [countdownConfig?.buttonLabelOverride, promoStatus]);

  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;

  const fallbackButton = (
    <Card border='none' boxShadow='none' padding='12px 8px 8px'>
      <Box flex={{ base: 1, md: 'unset' }}>
        <List
          alignItems='center'
          as='ol'
          display='flex'
          gap={3}
          mb={3}
          _after={{
            content: '""',
            display: 'block',
            height: '1px',
            backgroundColor: 'gray.200',
            flex: 1,
          }}
        >
          <RiskFreeListItem />
          {(selectedPlan === '4month' || selectedPlan === 'monthly') && <CancelAnytimeListItem />}
        </List>
        {renderButton(selectedPlan, buttonLabel)}
        <PriceCardSticker />
      </Box>
    </Card>
  );

  function render() {
    if (hasActiveSubscription || !countdownConfig || freeTrial.status == 'loading' || freeTrial.status === 'on') {
      return <>{fallbackButton}</>;
    }

    switch (countdownConfig.countdownTo) {
      case 'promoEndDate': {
        return (
          <CountdownToPromoEndDateLayout
            key={isTabVisible ? 1 : 0}
            countdownConfig={countdownConfig}
            fallbackSlot={fallbackButton}
            renderButton={() => renderButton(selectedPlan, buttonLabel)}
            selectedPlan={selectedPlan}
          />
        );
      }
      case 'couponExpiry': {
        return (
          <CountdownToCouponExpiryLayout
            key={isTabVisible ? 1 : 0}
            countdownConfig={countdownConfig}
            fallbackSlot={fallbackButton}
            renderButton={() => renderButton(selectedPlan, buttonLabel)}
            selectedPlan={selectedPlan}
          />
        );
      }
      default:
        return <>{fallbackButton}</>;
    }
  }

  return <>{render()}</>;
}

function RiskFreeListItem({ iconColor = 'blue.500' }: { iconColor?: string }) {
  const isEUVisitor = useIsEUVisitor();
  const riskFreePeriod = isEUVisitor ? '14-day' : '7-day';
  return (
    <ListItem alignItems='center' display='flex' flexShrink={0} fontSize='0.75rem' lineHeight='1.4'>
      <ListIcon as={CalendarIcon} color={iconColor} height='16px' width='16px' />
      {riskFreePeriod} risk-free
    </ListItem>
  );
}

function CancelAnytimeListItem({ iconColor = 'blue.500' }: { iconColor?: string }) {
  return (
    <Fade in={true} unmountOnExit>
      <ListItem alignItems='center' display='flex' flexShrink={0} fontSize='0.75rem' lineHeight='1.4'>
        <ListIcon as={CheckedIcon} color={iconColor} height='16px' width='16px' />
        Cancel anytime
      </ListItem>
    </Fade>
  );
}

function CountdownLayout({
  selectedPlan,
  renderButton,
  until,
  couponPercentOff,
  countdownConfig,
  couponCode,
  forcedButtonLabel,
  onButtonClick,
}: {
  renderButton: ButtonRenderProp;
  until: string;
  couponPercentOff?: number | null;
  countdownConfig: CountdownConfig;
  couponCode?: string | null;
  forcedButtonLabel?: string | null;
  onButtonClick?: () => Promise<void>;
  selectedPlan: BillingFrequency;
}) {
  const date = new Date(until);
  const { ms, isActive } = useCountdown(date);
  const [days, hours, minutes, seconds] = parseMs(ms);

  const blurb = countdownConfig?.blurb || 'Limited Time Offer!';
  const totalHours = days * 24 + hours;
  const hoursOnly = countdownConfig.displayMode === 'hms' && totalHours <= 100;
  const displayHours = hoursOnly ? totalHours : hours;

  return (
    <Flex
      alignItems='center'
      backgroundColor={isActive ? 'grey.900' : 'unset'}
      borderRadius='md'
      color='white'
      direction='column'
      gap={2}
      overflow='hidden'
      p={2}
      transition='background-color 300ms ease-in-out'
    >
      <Flex
        alignItems='center'
        as={Fade}
        flex='1'
        in={isActive}
        justifyContent='flex-end'
        width='100%'
        px={{
          base: 0,
          sm: 0,
        }}
        unmountOnExit
      >
        <Flex alignItems='center' gap={3} justifyContent='space-between' width='100%'>
          <Text fontSize='0.8125rem' fontWeight='600' lineHeight='1.4'>
            {blurb}
            <Box alignItems='center' as='span' color='orange.500' display='inline-flex' gap={1}>
              <FireIcon height='16px' width='16px' /> Save Now!
            </Box>
          </Text>
          <CountdownTimer
            days={days}
            hours={displayHours}
            hoursOnly={hoursOnly}
            minutes={minutes}
            seconds={seconds}
            containerProps={{
              bgColor: 'grey.700',
              padding: '4px 8px 8px',
              borderRadius: '4px',
              flex: '0 0 195px',
            }}
          />
        </Flex>
      </Flex>
      <List
        alignItems='center'
        alignSelf='normal'
        as='ol'
        display='flex'
        flex={1}
        gap={3}
        _after={{
          content: '""',
          display: 'block',
          height: '1px',
          backgroundColor: 'gray.200',
          flex: 1,
        }}
      >
        {(selectedPlan === '4month' || selectedPlan === 'monthly') && <CancelAnytimeListItem iconColor='white' />}
        <RiskFreeListItem iconColor='white' />
      </List>
      <Box flex={!isActive ? { base: 1, md: 'unset' } : undefined} width='100%'>
        {renderButton({
          isCountdownActive: isActive,
          couponPercentOff,
          countdownConfig,
          couponCode,
          forcedButtonLabel,
          until,
          onButtonClick,
        })}
      </Box>
    </Flex>
  );
}

export function CountdownToCouponExpiryLayout({
  renderButton,
  countdownConfig,
  fallbackSlot,
  selectedPlan,
}: {
  renderButton: ButtonRenderProp;
  countdownConfig: CountdownConfig;
  fallbackSlot: ReactNode;
  selectedPlan: BillingFrequency;
}) {
  const { data: promotion } = usePromotion();
  const { currency } = useCurrency();
  const { data: pricePlans, status: pricePlanStatus } = usePricePlans(currency);
  const clientReady = useClientRender();

  const individualMonthlyPrice = pricePlans?.prices?.monthly?.individual;
  const couponApplied = individualMonthlyPrice?.couponApplied;
  const couponExpiresAt = individualMonthlyPrice?.couponExpiresAt;
  const couponPercentOff = individualMonthlyPrice?.couponPercentOff;
  const couponCode = clientReady ? getCouponCodeFromCookie() : null;

  if (pricePlanStatus !== 'success' || !couponApplied || !couponExpiresAt || !couponPercentOff) {
    return <>{fallbackSlot}</>;
  }

  if (promotion?.type === 'personalized-discount' && couponCode && !isPersonalizedDiscountCoupon(couponCode)) {
    return <>{fallbackSlot}</>;
  }

  if (!isTimerShown(couponExpiresAt, countdownConfig.showTimerFromHours)) {
    return <>{fallbackSlot}</>;
  }

  return (
    <CountdownLayout
      countdownConfig={countdownConfig}
      couponPercentOff={couponPercentOff}
      forcedButtonLabel={`Take ${couponPercentOff}% Off`}
      renderButton={renderButton}
      selectedPlan={selectedPlan}
      until={couponExpiresAt}
    />
  );
}

export function CountdownToPromoEndDateLayout({
  selectedPlan,
  renderButton,
  countdownConfig,
  fallbackSlot,
}: {
  renderButton: ButtonRenderProp;
  countdownConfig: CountdownConfig;
  fallbackSlot: ReactNode;
  selectedPlan: BillingFrequency;
}) {
  const { data: promoData } = usePromotion();
  const { currency } = useCurrency();
  const pricePlans = usePricePlans(currency);
  const endDateTime = promoData?.endDateTime;
  const promoCode = promoData?.promoCode;

  async function buttonClickHandler() {
    if (promoCode && endDateTime) {
      setClientCookie(COUPON_COOKIE, promoCode.trim(), new Date(endDateTime));
      await pricePlans.refetch();
    }
  }

  if (!promoCode || !endDateTime) {
    return <>{fallbackSlot}</>;
  }

  if (!isTimerShown(endDateTime, countdownConfig.showTimerFromHours)) {
    return <>{fallbackSlot}</>;
  }

  return (
    <CountdownLayout
      countdownConfig={countdownConfig}
      couponCode={promoData.promoCode}
      forcedButtonLabel={countdownConfig.buttonLabelOverride}
      renderButton={renderButton}
      selectedPlan={selectedPlan}
      until={promoData.endDateTime}
      onButtonClick={buttonClickHandler}
    />
  );
}

export function isTimerShown(promoEndTimeStr: string, showTimerFromHours?: number | null): boolean {
  const now = new Date();
  const promoEndTime = new Date(promoEndTimeStr);
  const thresholdMs = (showTimerFromHours ?? 48) * 60 * 60 * 1000;
  const showTimerFrom = new Date(promoEndTime.getTime() - thresholdMs);
  return now < promoEndTime && now > showTimerFrom;
}
